import { useBrokerContextSafe } from '@/app/context';
import {
  useBrokers,
  useValidateSelectedBroker,
} from '@/features/brokers/hooks';
import type { Broker } from '@/services';
import { useIsBrokerUser } from '@/shared/hooks';
import { SearchFilterField } from '@/shared/ui';
import { useDebouncedValue } from '@/shared/utils/use-filters.hook';
import { Avatar, Button, Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, Users } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const BrokerContextSelector = memo(function BrokerContextSelector() {
  const brokerCtx = useBrokerContextSafe();
  const { isBrokerUser, isLoading: isLoadingBrokerUser } = useIsBrokerUser();

  // Nenhum hook pode ficar depois de um early return: o contexto pode alternar
  // de null para definido (HMR, error boundary, módulo duplicado) e o React
  // quebra o header com "Rendered more hooks than during the previous render".
  const selectedBroker = brokerCtx?.selectedBroker ?? null;
  const setSelectedBroker = brokerCtx?.setSelectedBroker;
  const clearSelectedBroker = brokerCtx?.clearSelectedBroker;

  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [search, setSearch] = useState('');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebouncedValue(search, 400);
  const { data: brokersData, isLoading } = useBrokers({
    search: debouncedSearch,
    pageSize: 1000,
    sortField: 'PartyName',
    sortType: 'asc',
  });

  const title = selectedBroker?.name ?? 'Todos Corretores';
  const subtitle = selectedBroker
    ? 'Corretor selecionado'
    : 'Sem filtro aplicado';

  const brokers = useMemo(() => {
    return brokersData?.data ?? [];
  }, [brokersData]);

  const total = brokersData?.meta?.total ?? 0;
  const isTruncated = total > brokers.length;

  // O corretor selecionado continua visível mesmo quando não cabe na página
  // devolvida pela API (o InsureMO corta a listagem em 1000 registros).
  const visibleBrokers = useMemo(() => {
    if (!selectedBroker || debouncedSearch) return brokers;
    const alreadyListed = brokers.some(b => b.code === selectedBroker.code);
    return alreadyListed ? brokers : [selectedBroker, ...brokers];
  }, [brokers, selectedBroker, debouncedSearch]);

  // A validação é por consulta ao código do corretor, não pela página da
  // listagem: "não está nesta página" não significa "não existe".
  const { isMissing } = useValidateSelectedBroker(selectedBroker);

  useEffect(() => {
    if (isMissing) clearSelectedBroker?.();
  }, [isMissing, clearSelectedBroker]);

  // Para usuários broker (master ou corretor): seleciona o próprio broker por padrão e não permite "Todos"
  useEffect(() => {
    if (!isBrokerUser || brokers.length === 0) return;
    // Com busca ativa a lista está filtrada; selecionar brokers[0] aqui trocaria
    // o corretor do usuário por outro qualquer.
    if (debouncedSearch) return;
    if (!selectedBroker && brokers.length > 0) {
      setSelectedBroker?.(brokers[0]);
    }
  }, [
    isBrokerUser,
    brokers,
    selectedBroker,
    setSelectedBroker,
    debouncedSearch,
  ]);

  const getBrokerInitials = useCallback((name: string) => {
    if (!name) return '';

    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, []);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
  }, []);

  const closeMenu = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => setIsOpen(false), 200);
  }, []);

  const toggleMenu = useCallback(() => {
    if (isOpen) closeMenu();
    else openMenu();
  }, [closeMenu, isOpen, openMenu]);

  const handleSelect = useCallback(
    (broker: Broker) => {
      setSelectedBroker?.(broker);
      setSearch('');
      closeMenu();
    },
    [closeMenu, setSelectedBroker]
  );

  const handleClear = useCallback(() => {
    clearSelectedBroker?.();
    setSearch('');
    closeMenu();
  }, [clearSelectedBroker, closeMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [isOpen]);

  if (!brokerCtx) return null;
  if (isLoadingBrokerUser) return null;

  return (
    <div ref={wrapperRef} className='relative'>
      <Button
        variant='ghost'
        size='small'
        onClick={toggleMenu}
        className='flex min-w-0 max-w-24 items-center gap-1 px-1 py-1 text-white hover:bg-alba-blue-500 sm:max-w-64 sm:gap-2 sm:px-2 md:max-w-80'
        aria-haspopup='menu'
        aria-expanded={isOpen}
      >
        {selectedBroker ? (
          <Avatar.Root
            size='sm'
            className='h-6 w-6 flex-shrink-0 bg-alba-blue-500'
          >
            <Avatar.Fallback className='text-[10px] font-semibold text-white'>
              {getBrokerInitials(selectedBroker.name)}
            </Avatar.Fallback>
          </Avatar.Root>
        ) : (
          <span className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#CFD2D3]'>
            <Users className='h-4 w-4 text-[#F9F9F9]' strokeWidth={1.6} />
          </span>
        )}
        <span className='flex min-w-0 flex-col items-start leading-tight'>
          <Text className='w-full truncate text-white text-xs font-medium sm:text-sm'>
            {title}
          </Text>
          <Text className='hidden w-full truncate text-alba-blue-100 text-xs sm:block'>
            {subtitle}
          </Text>
        </span>
        <ChevronDown className='h-4 w-4 flex-shrink-0' strokeWidth={1.6} />
      </Button>

      {isOpen && (
        <div
          className={`fixed left-1/2 -translate-x-1/2 top-[var(--app-header-bottom,64px)] sm:absolute sm:left-auto sm:translate-x-0 sm:right-0 sm:top-[3.2rem] z-70 w-[calc(100vw-1.5rem)] max-w-88 rounded-lg border border-zinc-200 bg-white shadow-md shadow-gray-50 overflow-hidden transition-all duration-200 ease-out ${
            isAnimating
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-2'
          }`}
        >
          {!isBrokerUser && (
            <div className='p-3 border-b border-zinc-200'>
              <SearchFilterField
                value={search}
                onChange={value => setSearch(String(value ?? ''))}
                placeholder='Buscar corretor...'
                className='w-full'
                inputRef={searchInputRef}
                name='broker-search'
                type='string'
              />
            </div>
          )}

          <div className='max-h-72 overflow-y-auto py-1'>
            {!isBrokerUser && (
              <button
                type='button'
                onClick={handleClear}
                className='w-full px-3 py-2 text-left hover:bg-alba-gray-50 transition-colors border-b border-zinc-100'
              >
                <div className='flex items-start gap-2'>
                  <span className='flex h-6 w-6 items-center justify-center rounded-full bg-[#CFD2D3]'>
                    <Users
                      className='h-4 w-4 text-[#F9F9F9]'
                      strokeWidth={1.6}
                    />
                  </span>
                  <div className='flex flex-col'>
                    <span className='text-sm font-semibold text-alba-black-500'>
                      Todos Corretores
                    </span>
                    <span className='text-xs text-alba-gray-600 '>
                      Sem filtro aplicado
                    </span>
                  </div>
                </div>
              </button>
            )}
            {isLoading ? (
              <div className='px-3 py-2 space-y-3'>
                {[1, 2, 3].map(i => (
                  <div key={i} className='flex items-start gap-2 animate-pulse'>
                    <div className='h-6 w-6 rounded-full bg-alba-gray-200' />
                    <div className='flex flex-col gap-1.5 flex-1'>
                      <div className='h-4 w-3/4 rounded bg-alba-gray-200' />
                      <div className='h-3 w-1/2 rounded bg-alba-gray-100' />
                    </div>
                  </div>
                ))}
              </div>
            ) : visibleBrokers.length === 0 ? (
              <div className='px-3 py-4 text-sm text-alba-gray-600'>
                Nenhum corretor encontrado.
              </div>
            ) : (
              <>
                {visibleBrokers.map(broker => (
                  <button
                    key={broker.id}
                    type='button'
                    onClick={() => handleSelect(broker)}
                    className='w-full px-3 py-2 text-left hover:bg-alba-gray-50 transition-colors'
                  >
                    <div className='flex items-start gap-2'>
                      <span className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#CFD2D3]'>
                        <Users
                          className='h-4 w-4 text-[#F9F9F9]'
                          strokeWidth={1.6}
                        />
                      </span>
                      <div className='flex flex-col'>
                        <span className='text-sm font-semibold text-alba-black-500'>
                          {broker.name}
                        </span>
                        <span className='text-xs text-alba-gray-600'>
                          {broker.email}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
                {isTruncated && (
                  <div className='border-t border-zinc-100 px-3 py-2 text-xs text-alba-gray-600'>
                    Mostrando {brokers.length} de {total} corretores — use a
                    busca para encontrar outros.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
